import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { countBlocking, findFamilyConflicts, type FamilyConflict } from "./findFamilyConflicts.js";
import {
  parseMoveToSetCsv,
  selectMoveToSetRows,
  type MoveToSetSelection,
  type ParsedMoveToSetCsv,
} from "./parseMoveToSetCsv.js";
import {
  useMoveToSetBatch,
  type MoveToSetFamilyCheck,
  type MoveToSetFamilyLink,
} from "./useMoveToSetBatch.js";

const DEFAULT_BATCH_SIZE = 500;

type MoveToSetFile = { name: string; parsed: ParsedMoveToSetCsv };

export type ApplyBlock =
  | { kind: "no_dry_run" }
  | { kind: "settings_changed" }
  | { kind: "conflicts"; blocking: number };

type DryRunCheck = {
  file: MoveToSetFile;
  includeNoCompanyId: boolean;
  batchSize: number;
  familyLinks: MoveToSetFamilyLink[];
  conflicts: FamilyConflict[];
};

type MoveToSetBatchApi = ReturnType<typeof useMoveToSetBatch>;

type MoveToSetRunState = Omit<MoveToSetBatchApi, "start" | "retryFailed" | "reset">;

type MoveToSetRunContextValue = MoveToSetRunState & {
  batchSize: number;
  setBatchSize: (size: number) => void;
  file: MoveToSetFile | null;
  parsing: boolean;
  parseError: string | null;
  selectFile: (selected: File) => Promise<void>;
  includeNoCompanyId: boolean;
  setIncludeNoCompanyId: (include: boolean) => void;
  selection: MoveToSetSelection | null;
  runSkippedCount: number;
  conflicts: FamilyConflict[] | null;
  applyBlock: ApplyBlock | null;
  startDryRun: () => Promise<void>;
  startApply: () => Promise<void>;
  retryFailedRows: () => Promise<void>;
  sectionMounted: boolean;
  registerSection: () => () => void;
};

const MoveToSetRunContext = createContext<MoveToSetRunContextValue | null>(null);

function runState({ start, retryFailed, reset, ...state }: MoveToSetBatchApi): MoveToSetRunState {
  return state;
}

function familyCheckFor(check: DryRunCheck, selection: MoveToSetSelection): MoveToSetFamilyCheck {
  const fileRows = {
    rows: selection.rows,
    skipped: selection.skipped,
    invalid: check.file.parsed.invalid,
  };
  return links => findFamilyConflicts([...check.familyLinks, ...links], fileRows);
}

export function MoveToSetRunProvider({ children }: { children: ReactNode }) {
  const batch = useMoveToSetBatch();
  const { start, reset, retryFailed } = batch;
  const [batchSize, setBatchSize] = useState<number>(DEFAULT_BATCH_SIZE);
  const [file, setFile] = useState<MoveToSetFile | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [includeNoCompanyId, setIncludeNoCompanyId] = useState(false);
  const [runSkippedCount, setRunSkippedCount] = useState(0);
  const [dryRunCheck, setDryRunCheck] = useState<DryRunCheck | null>(null);
  const [mountedSections, setMountedSections] = useState(0);
  const busyRef = useRef(false);

  const selection = useMemo(
    () => (file ? selectMoveToSetRows(file.parsed, includeNoCompanyId) : null),
    [file, includeNoCompanyId],
  );

  const applyBlock = useMemo((): ApplyBlock | null => {
    if (!dryRunCheck || dryRunCheck.file !== file) return { kind: "no_dry_run" };
    if (
      dryRunCheck.includeNoCompanyId !== includeNoCompanyId ||
      dryRunCheck.batchSize !== batchSize
    ) {
      return { kind: "settings_changed" };
    }
    const blocking = countBlocking(dryRunCheck.conflicts);
    return blocking > 0 ? { kind: "conflicts", blocking } : null;
  }, [dryRunCheck, file, includeNoCompanyId, batchSize]);
  const applyBlockRef = useRef(applyBlock);
  applyBlockRef.current = applyBlock;

  const exclusive = useCallback(async (task: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await task();
    } finally {
      busyRef.current = false;
    }
  }, []);

  const selectFile = useCallback(
    async (selected: File) => {
      if (busyRef.current) return;
      reset();
      setParsing(true);
      setParseError(null);
      setFile(null);
      setDryRunCheck(null);
      setIncludeNoCompanyId(false);
      try {
        setFile({ name: selected.name, parsed: await parseMoveToSetCsv(selected) });
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse CSV");
      } finally {
        setParsing(false);
      }
    },
    [reset],
  );

  const startDryRun = useCallback(
    () =>
      exclusive(async () => {
        if (!file || !selection) return;
        const checked = { file, includeNoCompanyId, batchSize };
        setDryRunCheck(null);
        setRunSkippedCount(selection.skipped.length);
        const summary = await start(selection.rows, { batchSize, dryRun: true });
        if (!summary.finished) return;
        const conflicts = findFamilyConflicts(summary.familyLinks, {
          rows: selection.rows,
          skipped: selection.skipped,
          invalid: file.parsed.invalid,
        });
        setDryRunCheck({ ...checked, familyLinks: summary.familyLinks, conflicts });
      }),
    [exclusive, start, file, selection, includeNoCompanyId, batchSize],
  );

  const startApply = useCallback(
    () =>
      exclusive(async () => {
        if (applyBlockRef.current !== null || !dryRunCheck || !selection) return;
        setRunSkippedCount(selection.skipped.length);
        const summary = await start(selection.rows, {
          batchSize,
          dryRun: false,
          familyCheck: familyCheckFor(dryRunCheck, selection),
        });
        if (summary.blocked) setDryRunCheck(null);
      }),
    [exclusive, start, dryRunCheck, selection, batchSize],
  );

  const retryFailedRows = useCallback(
    () =>
      exclusive(async () => {
        if (applyBlockRef.current !== null || !dryRunCheck || !selection) return;
        const summary = await retryFailed(batchSize, familyCheckFor(dryRunCheck, selection));
        if (summary?.blocked) setDryRunCheck(null);
      }),
    [exclusive, retryFailed, dryRunCheck, selection, batchSize],
  );

  const registerSection = useCallback(() => {
    setMountedSections(count => count + 1);
    return () => setMountedSections(count => count - 1);
  }, []);

  const conflicts =
    dryRunCheck && (applyBlock === null || applyBlock.kind === "conflicts")
      ? dryRunCheck.conflicts
      : null;

  const value = useMemo(
    (): MoveToSetRunContextValue => ({
      ...runState(batch),
      batchSize,
      setBatchSize,
      file,
      parsing,
      parseError,
      selectFile,
      includeNoCompanyId,
      setIncludeNoCompanyId,
      selection,
      runSkippedCount,
      conflicts,
      applyBlock,
      startDryRun,
      startApply,
      retryFailedRows,
      sectionMounted: mountedSections > 0,
      registerSection,
    }),
    [
      batch,
      batchSize,
      file,
      parsing,
      parseError,
      selectFile,
      includeNoCompanyId,
      selection,
      runSkippedCount,
      conflicts,
      applyBlock,
      startDryRun,
      startApply,
      retryFailedRows,
      mountedSections,
      registerSection,
    ],
  );

  return <MoveToSetRunContext.Provider value={value}>{children}</MoveToSetRunContext.Provider>;
}

export function useMoveToSetRun(): MoveToSetRunContextValue {
  const value = useContext(MoveToSetRunContext);
  if (!value) throw new Error("useMoveToSetRun must be used inside MoveToSetRunProvider");
  return value;
}

export function useOptionalMoveToSetRun(): MoveToSetRunContextValue | null {
  return useContext(MoveToSetRunContext);
}
